import {css} from "lit";

export const buttonStyles = css`
	/*********************************************
	/*********** General Button Styles ***********
	**********************************************/
	button {
		border: none;
		border-radius: 0;
		padding: 0;
		background-color: inherit;
		cursor: pointer;
	}
	button:hover, button:focus, button:active {
		transition: all;
		transition-duration: .1s;
	}

	.btn-primary, .btn-secondary, .btn-tertiary, .btn-yellow, .btn-red, .btn-purple, .btn-purple-inverted {
		font-family: var(--pn-headings-font-family);
		font-weight: bold;
		border-width: 1px;
		border-style: solid;
		font-size: 1.125rem;
		line-height: 20px;
		border-radius: 30px;
		padding: 0.9375rem 2.1875rem;
		color: var(--pn-button-default-text-color);
	}
	.btn-primary:hover, .btn-secondary:hover, .btn-tertiary:hover, .btn-yellow:hover, .btn-red:hover, .btn-purple:hover, .btn-purple-inverted:hover,
	.btn-primary:focus, .btn-secondary:focus, .btn-tertiary:focus, .btn-yellow:focus, .btn-red:focus, .btn-purple:focus, .btn-purple-inverted:focus {
		color: var(--pn-button-default-text-color);
	}

	.btn-primary:disabled, .btn-secondary:disabled, .btn-tertiary:disabled, .btn-yellow:disabled, .btn-red:disabled, .btn-purple:disabled, .btn-purple-inverted:disabled {
		background-color: var(--pn-button-disabled-color);
		border: 1px solid var(--pn-button-disabled-color);
		color: var(--pn-button-default-text-color);
	}

	/* Loading Animation (Ellipses) */
	.btn-loading-dots {
		display: none;
	}

	button.loading {
		position: relative;
		outline: none;
		color: var(--pn-button-loading-text-color);
	}
	button.loading .btn-loading-dots {
		display: block;
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		margin: 0;
		padding-top: 5px;
	}
	.loading .btn-loading-dots span {
		color: var(--pn-button-default-text-color);
		font-size: 50px;
		position: relative;
		margin: 0 5px;
		animation: ellipses-jump 1s infinite ease-in-out;
	}
	.loading .btn-loading-dots span:first-of-type {
		margin-left: 20px;
	}
	.loading .btn-loading-dots span:nth-of-type(2) {
		animation-delay: .33s;
	}
	.loading .btn-loading-dots span:last-of-type {
		margin-right: 20px;
		animation-delay: .66s;
	}

	@keyframes ellipses-jump {
		0% {
			top: 0;
		}
		50% {
			top: -10px;
		}
		100% {
			top: 0;
		}
	}


	.btn-primary {
		color: var(--pn-color-white);
		background-color: var(--pn-button-primary-color);
		border-color: var(--pn-button-primary-color);
	}
	.btn-primary:hover, .btn-primary:focus {
		background-color: var(--pn-button-primary-color-hover);
		border-color: var(--pn-button-primary-color-hover);
	}
	.btn-primary:active {
		background-color: var(--pn-button-primary-color-active);
		border-color: var(--pn-button-primary-color-active);
	}


	.btn-secondary {
		color: var(--pn-button-secondary-color);
		background-color: transparent;
		border-color: var(--pn-button-secondary-color);
		box-shadow: inset 0px 0px 0px 1px fade(var(--pn-button-secondary-color), 50%);
	}
	.btn-secondary:not(:disabled):hover, .btn-secondary:not(:disabled):focus {
		color: var(--pn-button-secondary-color-hover);
		border-color: var(--pn-button-secondary-color-hover);
		box-shadow: inset 0px 0px 0px 2px var(--pn-button-secondary-color-hover);
	}

	.btn-secondary:active {
		color: var(--pn-button-secondary-color-active);
		border-color: var(--pn-button-secondary-color-active);
	}
	.btn-secondary.loading:not(:disabled) {
		color: transparent;
	}
	.btn-secondary.loading:not(:disabled) .btn-loading-dots span {
		color:  var(--pn-button-secondary-color);
	}
	
	.btn-small {
		padding: 10px 21px;
		font-size: 14px;
		line-height: 18px;
		font-weight: 600;
	}

	.btn-purple {
		padding: 13px 16px;
		font-size: 14px;
		line-height: 20px;
		font-weight: 600;
		color: var(--pn-color-white);
		background-color: var(--pn-button-purple-color);
		border-color: var(--pn-button-purple-color);
	}

	.btn-purple:hover {
		background-color: var(--pn-button-purple-color-hover);
		border-color: var(--pn-button-purple-color-hover);
	}

	.btn-purple:focus {
		// Use this background color for now, as the box-shadow doesn't provide
		// enough contrast on its own (Figma)
		background-color: var(--pn-button-purple-color-hover);
	}

	.btn-purple:disabled {
		border: 1px solid var(--pn-button-purple-color-disabled);
		background: var(--pn-button-purple-color-disabled);
	}

	.btn-purple i[class*=icon-] {
		display: inline-block;
		height: 20px;
		width: 21px;
		margin: -2px 8px -4px -4px;
	}

	.btn-purple-inverted {
		color: var(--pn-button-purple-color);
		background-color: var(--pn-color-white);
		border-color: var(--pn-button-purple-color);
	}
	.btn-purple-inverted:hover {
		color: var(--pn-button-purple-color-hover);
		border-color: var(--pn-button-purple-color-hover);
	}

	.btn-purple-inverted:focus {
		// Use this background color for now, as the box-shadow doesn't provide
		// enough contrast on its own (Figma)
		color: var(--pn-button-purple-color-hover);
		border-color: var(--pn-button-purple-color-hover);
	}

	.btn-purple-inverted:disabled {
		border: 1px solid var(--pn-button-purple-color-disabled);
	}

	// Masked icons
	.btn-purple-inverted .icon-mask {
		font-size: 24px;
		background-color: var(--pn-button-purple-color);
	}

	.btn-purple-inverted:hover .icon-mask, .btn-purple-inverted:focus .icon-mask {
		background-color: var(--pn-button-purple-color-hover);
	}

	.btn-auth {
		display: flex;
		background-color: var(--pn-button-auth-bg-color);
		border: 1px solid;
		border-color: var(--pn-button-auth-border-color);
		padding: 12px 4px 12px 24px;
		border-radius: 6px;
		width: 100%;
		color: var(--pn-button-auth-text-color);
		font-family: var(--pn-font-family-base);
		font-weight: 500;
		font-size: 14px;
		line-height: 18px;
		justify-content: center;
		align-items: center;
		cursor: pointer;
		box-sizing: border-box;
		position: relative;
	}

	.btn-auth:hover, .btn-auth:focus {
		outline-style: none;
		box-shadow: none;
		background-color: var(--pn-button-auth-bg-color-hover);
		border-color: var(--pn-button-auth-border-color-hover);
	}

	.btn-auth:active {
		background-color: var(--pn-button-auth-bg-color-active);
		border-color: var(--pn-button-auth-border-color-active);
	}

	.btn-auth:disabled {
		opacity: .6;
	}

	.btn-auth:disabled:hover,
	.btn-auth:disabled:focus,
	.btn-auth:disabled:active {
		background-color: var(--pn-button-auth-bg-color);
		border-color: var(--pn-button-auth-border-color);
	}

	.btn-auth img {
		position: absolute;
		height: 18px;
		width: 18px;
		left: 14px;
	}

	.btn-auth div {
		margin-top: 2px;
	}

	/* TODO: other button types (see pn-www) */



	/*********************************************
	/********** Link type text buttons ***********
	**********************************************/
	[class*="btn-link"] {
		font-family: var(--pn-font-family-base);
		font-size: inherit;
		font-weight: 500;
		border: none;
		background-color: transparent;
		padding: 5px 0;
	}
	[class*="btn-link"]:hover, [class*="btn-link"]:focus {
		text-decoration: underline;
	}

	.btn-link {
		color: var(--pn-button-link-default-color);
	}
	.btn-link:hover, .btn-link:focus {
		color: var(--pn-button-link-default-color-hover);
		transition: all;
		transition-duration: 0.2s;
	}
	.btn-link:active {
		color: var(--pn-button-link-default-color-active);
	}

	.btn-link-blue {
		color: var(--pn-button-link-blue-color);
	}
	.btn-link-blue:hover, .btn-link-blue:focus {
		color: var(--pn-button-link-blue-color-hover);
	}
	.btn-link-blue:active {
		color: var(--pn-button-link-blue-color-active);
	}

	.btn-link-bordered {
		border: 1px solid var(--pn-button-link-default-color);
		border-radius: 10px;
		padding: 5px 8px;
	}
	.btn-link-bordered:hover, .btn-link-bordered:focus {
		border-color: var(--pn-button-link-default-color-hover);
	}
	.btn-link-bordered:active {
		border-color: var(--pn-button-link-default-color-active);
	}
`;
